import React, {useEffect, useState, useCallback, useMemo, useRef} from 'react';
import {Form, Input, Select, Switch, Typography, Row, Col, Tooltip, TreeSelect} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {ModelDTO, VEAPActivity} from 'hemwb-api';
import {ModelTreeNode} from '../../../../store/modelFile';
import {trackMetaDataTabEvent} from '../../../../tracking/tracking';
import {
  buildFlatHierarchy,
  ModelFlatNode,
  replaceNodeInFlatHierarchy,
  useModelFlatHierarchy,
} from '../../../../store/modelLinks';
import {canBeParentOfModel, isModelCreating, modifyModelMetadata, useModels} from '../../../../store/model';
import {useModelMetadataActiveOptions} from '../../../../store/modelMetadata';
import {
  fields as metadataFieldLabels,
  hardCodedOptions,
  requiredFields as metadataRequiredFields,
  FieldKey,
} from '../../model/common/ModelEdit/ModelEditMetadata/constants';
import {
  useVeevaDocMetadataLoaderWithParams,
  useVeevaMdpLoaderWithParams,
  useVeevaMdpModelLanguageLoaderWithParams,
  useVeevaVeapLoaderWithParams,
} from '../../../../store/veeva/hooks';
import LineageSelectionAntd from './LineageSelectionAntd';
import {
  isEmptyValue,
  resolveCountryGlobal,
  VeevaDocMetadata,
} from '../../model/common/ModelEdit/ModelEditMetadata/veevaAutofillUtils';
//import {tid} from '../../../../testUtils';
import styles from './StepMetadataRedesigned.module.scss';

const {Text} = Typography;
const {TextArea} = Input;
const {Option} = Select;

/**
 * Highlights matching text in a string by wrapping matches in a span with teal background
 */
const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!text || !searchTerm.trim()) {
    return text;
  }

  const lowerText = text.toLowerCase();
  const lowerSearch = searchTerm.toLowerCase();
  const startIdx = lowerText.indexOf(lowerSearch);

  if (startIdx === -1) {
    return text;
  }

  return (
    <>
      {text.substring(0, startIdx)}
      <span style={{backgroundColor: '#00a0a0', fontWeight: 500}}>
        {text.substring(startIdx, startIdx + searchTerm.length)}
      </span>
      {text.substring(startIdx + searchTerm.length)}
    </>
  );
};

interface StepMetadataRedesignedProps {
  model: ModelDTO;
  files: ModelTreeNode[];
  onChange: (updatedModel: ModelDTO) => void;
  onSubmit: (updatedModel: ModelDTO) => void;
  onRevopsNameChange?: (revopsName: string) => void;
  onStewardReset?: (steward: {id?: string; email?: string; name?: string}) => void;
  showErrors?: boolean;
  onBack?: () => void;
  onNext?: () => void;
  buttons?: (params: {
    backText?: string;
    onNext?: () => void;
    onBack?: () => void;
    disableNext?: boolean;
    nextHtmlType?: 'button' | 'submit' | 'reset';
  }) => React.ReactNode;
}

const StepMetadataRedesigned: React.FC<StepMetadataRedesignedProps> = ({
  model,
  files,
  onChange,
  onSubmit,
  onRevopsNameChange,
  onStewardReset,
  //showErrors = false,
  onBack,
  //onNext,
  buttons,
}) => {
  const [form] = Form.useForm();
  const trainingAndPublicationLinkValue = Form.useWatch('training_and_publication_link', form);
  const [isGlobalModel, setIsGlobalModel] = useState(false);
  const [enableWebModel, setEnableWebModel] = useState(false);
  const [lineageMode, setLineageMode] = useState<'parent' | 'root'>('parent');

  const options = useModelMetadataActiveOptions();
  const loadVeap = useVeevaVeapLoaderWithParams();
  const loadMdp = useVeevaMdpLoaderWithParams();
  const loadMdpModelLanguage = useVeevaMdpModelLanguageLoaderWithParams();
  const loadDocMetadata = useVeevaDocMetadataLoaderWithParams();
  const [revopsOptions, setRevopsOptions] = useState<VEAPActivity[]>([]);
  const [revopsLoading, setRevopsLoading] = useState(false);
  const [revopsSearchTerm, setRevopsSearchTerm] = useState('');
  const [revopsDropdownOpen, setRevopsDropdownOpen] = useState(false);
  const [mdp, setMdp] = useState<string[]>();
  const [languageAutoPopulated, setLanguageAutoPopulated] = useState(false);
  const userEditedCountryOrGlobalRef = useRef(false);
  const userEditedLanguageRef = useRef(false);

  const loadRevopsList = useCallback(
    (searchTerm?: string, isIdLookup = false) => {
      if (!searchTerm) {
        setRevopsOptions([]);
        setRevopsSearchTerm('');
        return Promise.resolve([] as VEAPActivity[]);
      }

      setRevopsSearchTerm(searchTerm);
      setRevopsLoading(true);
      return loadVeap([searchTerm])
        .then((response) => {
          const veapItems = Array.isArray(response) ? (response as unknown as VEAPActivity[]) : [];
          // Filter to only show items that:
          // 1. Start with MDA (the VEAP ID prefix)
          // 2. When searching by name (user-typed search), also require the name to contain the search term.
          //    When loading by ID (isIdLookup=true), skip the name-contains check because the search term
          //    is a VEAP activity ID (e.g. "VEAP-OLD"), not a name prefix.
          const mdaItems = veapItems.filter(
            (item) =>
              item.name?.startsWith('MDA') &&
              (isIdLookup || item.name?.toUpperCase().includes(searchTerm.toUpperCase())),
          );
          setRevopsOptions(mdaItems);
          return mdaItems;
        })
        .finally(() => setRevopsLoading(false));
    },
    [loadVeap],
  );

  const loadMdpList = useCallback(
    (veapId?: string) => {
      if (veapId) {
        return loadMdp([veapId]).then((response) => {
          if (Array.isArray(response)) {
            const urls = response.map((r: any) => r.url);
            setMdp(urls);
            return urls;
          }
          setMdp([]);
          return [];
        });
      } else {
        setMdp([]);
        return Promise.resolve([] as string[]);
      }
    },
    [loadMdp],
  );

  const models = useModels();
  const {uuid} = model;
  const [rootId, setRootId] = useState(model.uuid || model.metadata?.tree_root_id || '');
  const hierarchy = useModelFlatHierarchy(rootId);
  const [descendantHierarchy, setDescendantHierarchy] = useState<ModelFlatNode[]>();
  const [workingHierarchy, setWorkingHierarchy] = useState<ModelFlatNode[]>([]);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [pendingRootSelection, setPendingRootSelection] = useState<string | null>(null);

  const normalizeProductSet = useCallback((product?: string) => {
    return (product || '')
      .split('|')
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean)
      .sort()
      .join('|');
  }, []);

  const matchLineageMetadataForRedesign = useCallback(
    (candidate: ModelDTO, currentModel: ModelDTO) => {
      return (
        normalizeProductSet(candidate.metadata?.product) === normalizeProductSet(currentModel.metadata?.product) &&
        (candidate.metadata?.disease || '').trim().toLowerCase() ===
          (currentModel.metadata?.disease || '').trim().toLowerCase() &&
        (candidate.metadata?.therapeutic_area || '').trim().toLowerCase() ===
          (currentModel.metadata?.therapeutic_area || '').trim().toLowerCase()
      );
    },
    [normalizeProductSet],
  );

  const [node, setNode] = useState<ModelFlatNode<ModelDTO>>({
    model: {...model, metadata: {...model.metadata}} as ModelDTO,
    parentId: model.metadata?.tree_parent_id || '',
    originalParentId: isModelCreating(model) ? '' : model.metadata?.tree_parent_id || '',
  });

  const isSameFlatHierarchy = useCallback((left: ModelFlatNode[] = [], right: ModelFlatNode[] = []) => {
    if (left.length !== right.length) {
      return false;
    }

    return left.every(
      (item, index) =>
        item.model.uuid === right[index].model.uuid &&
        item.parentId === right[index].parentId &&
        item.originalParentId === right[index].originalParentId,
    );
  }, []);

  // Update node when model changes
  useEffect(() => {
    setNode({
      model: {...model, metadata: {...model.metadata}} as ModelDTO,
      parentId: model.metadata?.tree_parent_id || '',
      originalParentId: isModelCreating(model) ? '' : model.metadata?.tree_parent_id || '',
    });
  }, [model]);

  // Update rootId when model metadata changes (to trigger hierarchy re-fetch)
  useEffect(() => {
    const newRootId = model.uuid || model.metadata?.tree_root_id || '';
    if (newRootId && newRootId !== rootId) {
      setRootId(newRootId);
    }
  }, [model.uuid, model.metadata?.tree_root_id]);

  // Fallback to metadata-based parent suggestions when hierarchy is not available or only contains current draft
  useEffect(() => {
    if (!isModelCreating(model) || !models) {
      return;
    }

    const hierarchyOnlyCurrent = !!hierarchy && hierarchy.every((item) => item.model.uuid === model.uuid);
    if (hierarchy && !hierarchyOnlyCurrent) {
      return;
    }

    const allModels = Object.values(models);
    const matchingModels = allModels.filter(
      (item) =>
        item.uuid !== model.uuid &&
        matchLineageMetadataForRedesign(item as ModelDTO, model) &&
        canBeParentOfModel(item, model),
    );

    if (matchingModels.length > 0) {
      const tempHierarchy: ModelFlatNode[] = matchingModels.map((item) => ({
        model: item,
        parentId: item.metadata?.tree_parent_id || '',
        originalParentId: item.metadata?.tree_parent_id || '',
      }));
      setWorkingHierarchy([...tempHierarchy, node]);
      setDescendantHierarchy([]);
    }
  }, [model, models, hierarchy, node, matchLineageMetadataForRedesign]);

  // Set descendants on load
  useEffect(() => {
    if (!descendantHierarchy) {
      if (hierarchy) {
        const nodeFound = hierarchy.find((n) => n.model.uuid === uuid);
        if (nodeFound) {
          setDescendantHierarchy(
            buildFlatHierarchy(
              node.model,
              hierarchy.map((n) => n.model),
            ).filter((n) => n.model.uuid !== uuid),
          );
        }
      } else if (node && node.originalParentId === '') {
        setDescendantHierarchy([]);
      }
    }
  }, [uuid, node, hierarchy, descendantHierarchy]);

  useEffect(() => {
    if (hierarchy && descendantHierarchy) {
      const hierarchyOnlyCurrent = hierarchy.every((item) => item.model.uuid === node.model.uuid);
      const hasMetadataFallbackSuggestions = workingHierarchy.some((item) => item.model.uuid !== node.model.uuid);
      if (isModelCreating(model) && hierarchyOnlyCurrent && hasMetadataFallbackSuggestions) {
        return;
      }

      if (rootId === node.model.metadata?.tree_root_id && !hierarchy.some((n) => n.model.uuid === node.model.uuid)) {
        const nextHierarchy = [...hierarchy, node, ...descendantHierarchy];
        setWorkingHierarchy((previousHierarchy) =>
          isSameFlatHierarchy(previousHierarchy, nextHierarchy) ? previousHierarchy : nextHierarchy,
        );
      } else {
        const n = hierarchy.find((n) => n.model.uuid === uuid);
        if (
          n &&
          (n.model.uuid !== node.model.uuid ||
            n.parentId !== node.parentId ||
            n.originalParentId !== node.originalParentId)
        ) {
          setNode(n as ModelFlatNode<ModelDTO>);
        }
        setWorkingHierarchy((previousHierarchy) =>
          isSameFlatHierarchy(previousHierarchy, hierarchy) ? previousHierarchy : hierarchy,
        );
      }

      // Auto-switch to parent mode once new hierarchy is loaded after root selection
      if (pendingRootSelection && hierarchy.length > 0) {
        setLineageMode('parent');
        setPendingRootSelection(null);
      }
    } else if (descendantHierarchy?.length === 0) {
      setWorkingHierarchy((previousHierarchy) => {
        // Don't overwrite metadata-based fallback suggestions for a new model being created
        if (isModelCreating(model) && previousHierarchy.some((item) => item.model.uuid !== node.model.uuid)) {
          return previousHierarchy;
        }
        return isSameFlatHierarchy(previousHierarchy, [node]) ? previousHierarchy : [node];
      });
    }
  }, [
    hierarchy,
    descendantHierarchy,
    rootId,
    model,
    node,
    workingHierarchy,
    uuid,
    pendingRootSelection,
    isSameFlatHierarchy,
  ]);

  const isModelInHierarchy = useMemo(() => {
    const lineageHierarchy = workingHierarchy.length > 0 ? workingHierarchy : [node];
    if (node.model.metadata?.tree_root_id && !node.model.metadata?.tree_parent_id && lineageHierarchy.length > 1) {
      return false;
    }
    return lineageHierarchy.some((n) => n.model.uuid === model.uuid);
  }, [workingHierarchy, model, node]);

  const lineageHierarchy = useMemo(() => {
    // Include both the working hierarchy and the full hierarchy to ensure all available parents are available for selection
    // This is important when "Create New Lineage" resets workingHierarchy to just [newNode, ...descendantHierarchy]
    const baseHierarchy = workingHierarchy.length > 0 ? workingHierarchy : [node];

    // If we have a hierarchy from the root, include all models from it as well
    if (hierarchy && hierarchy.length > 0) {
      // const hierarchyUuids = new Set(hierarchy.map((h) => h.model.uuid));
      const baseUuids = new Set(baseHierarchy.map((b) => b.model.uuid));

      // Combine both hierarchies, avoiding duplicates
      const combined = [...baseHierarchy];
      hierarchy.forEach((item) => {
        if (!baseUuids.has(item.model.uuid)) {
          combined.push(item);
        }
      });

      return combined;
    }

    return baseHierarchy;
  }, [workingHierarchy, node, hierarchy]);

  const handleParentSelect = useCallback(
    (newParentNode: ModelFlatNode<ModelDTO>) => {
      const isNewRoot = newParentNode.model.metadata?.tree_root_id !== node.model.metadata?.tree_root_id;
      const newNode = {
        ...node,
        parentId: newParentNode.model.uuid,
        model: modifyModelMetadata(node.model, {
          tree_root_id: newParentNode.model.metadata?.tree_root_id,
          tree_parent_id: newParentNode.model.uuid,
        }),
      };

      setWorkingHierarchy([
        ...replaceNodeInFlatHierarchy(workingHierarchy, newNode),
        ...(isNewRoot ? descendantHierarchy! : []),
      ]);
      setNode(newNode);
      setSubmitClicked(false);
      onChange?.(newNode.model);
    },
    [workingHierarchy, descendantHierarchy, node, onChange],
  );

  const handleNewHierarchy = useCallback(() => {
    const newNode = {
      ...node,
      parentId: '',
      originalParentId: '',
      model: modifyModelMetadata(node.model, {
        tree_root_id: node.model.uuid,
        tree_parent_id: '',
      }),
    };

    setWorkingHierarchy([newNode, ...descendantHierarchy!]);
    setNode(newNode);
    setLineageMode('parent');
    setSubmitClicked(false);
    onChange?.(newNode.model);
  }, [node, descendantHierarchy, onChange]);

  const handleClearParent = useCallback(() => {
    const newNode = {
      ...node,
      parentId: '',
      model: modifyModelMetadata(node.model, {
        tree_parent_id: '',
      }),
    };

    setWorkingHierarchy(replaceNodeInFlatHierarchy(workingHierarchy, newNode));
    setNode(newNode);
    setSubmitClicked(false);
    onChange?.(newNode.model);
  }, [node, workingHierarchy, onChange]);

  const handleRootSelect = useCallback((selectedRootId: string) => {
    setRootId(selectedRootId);
    setPendingRootSelection(selectedRootId);
    setSubmitClicked(false);
  }, []);

  useEffect(() => {
    trackMetaDataTabEvent();
  }, []);

  // Initialize isGlobalModel on first load or when country changes from the model
  useEffect(() => {
    // Only initialize if user hasn't manually edited the global switch yet
    if (!userEditedCountryOrGlobalRef.current) {
      const hasCountry = !!model.metadata?.country;
      const shouldBeGlobal = model.metadata?.global === 'true' || !hasCountry;
      setIsGlobalModel(shouldBeGlobal);
    }
  }, [model.metadata?.country, model.metadata?.global]);

  useEffect(() => {
    // Resolve the REV/OPS ID: new flow stores it in revops_id, old flow stored it in veap_id
    const resolvedRevopsId = model.metadata?.revops_id || model.metadata?.veap_id;

    // Initialize form values from model (leave revops_id empty initially so spinner shows)
    form.setFieldsValue({
      revops_id: undefined,
      revops_name: undefined,
      study_number: model.metadata?.study_number,
      type: model.metadata?.type,
      information_classification: model.metadata?.information_classification,
      product: model.metadata?.product ? model.metadata.product.split('|') : [],
      disease: model.metadata?.disease,
      therapeutic_area: model.metadata?.therapeutic_area,
      model_type: model.metadata?.model_type,
      country: model.metadata?.country,
      suffix: model.metadata?.suffix,
      objective_of_model: model.metadata?.objective_of_model,
      notes: model.metadata?.notes,
      training_and_publication_link: model.metadata?.training_and_publication_link,
      version_release_of_model: model.metadata?.version_release_of_model,
      version_log: model.metadata?.version_log,
      changelog_file: model.metadata?.changelog_file,
      model_development_plan: model.metadata?.model_development_plan,
      stage_of_model_development: model.metadata?.stage_of_model_development,
    });

    setEnableWebModel(model.metadata?.platform === 'WebModel 2.0');

    // Load RevOps + MDP if revops_id (or legacy veap_id) is already set on the model
    if (resolvedRevopsId) {
      // Load the revops list by ID to get the name and populate the form
      // Pass isIdLookup=true so the name-contains filter is skipped — the search term here
      // is a VEAP activity ID, not a user-typed name search.
      loadRevopsList(resolvedRevopsId, true).then((loadedOptions) => {
        // Once options are loaded, set the form field to the first option's ID and name so Select displays the name
        if (Array.isArray(loadedOptions) && loadedOptions.length > 0) {
          form.setFieldValue('revops_id', loadedOptions[0].id);
          form.setFieldValue('revops_name', loadedOptions[0].name); // Store the name for review screen
          onRevopsNameChange?.(loadedOptions[0].name);
          // Also load MDP after options are available
          loadMdpList(resolvedRevopsId);
        }
      });
    }
  }, [model, form, loadRevopsList, loadMdpList, onRevopsNameChange]);

  const handleFormChange = (changedValues: any, allValues: any, globalOverride?: boolean) => {
    if ('type' in changedValues) {
      setLanguageAutoPopulated(false);
    }

    const selectedCountry = changedValues?.country;
    const selectedGlobalFromCountry = selectedCountry === 'Global';
    const shouldForceGlobalOff = isGlobalModel && !!selectedCountry && selectedCountry !== 'Global';
    let effectiveIsGlobalModel = shouldForceGlobalOff ? false : isGlobalModel;

    if (selectedGlobalFromCountry) {
      effectiveIsGlobalModel = true;
      if (!isGlobalModel) {
        setIsGlobalModel(true);
      }
    }

    if (typeof globalOverride === 'boolean') {
      effectiveIsGlobalModel = globalOverride;
      if (globalOverride !== isGlobalModel) {
        setIsGlobalModel(globalOverride);
      }
    }

    if (shouldForceGlobalOff) {
      setIsGlobalModel(false);
    }

    const updatedModel = {
      ...node.model,
      metadata: {
        ...node.model.metadata,
        ...Object.keys(allValues).reduce((acc, key) => {
          // Form field names now match metadata keys directly
          // Convert product array to pipe-delimited string
          if (key === 'product' && Array.isArray(allValues[key])) {
            acc['product'] = allValues[key].join('|');
          } else {
            acc[key] = allValues[key];
          }
          return acc;
        }, {} as any),
        // Mirror revops_id → veap_id so the backend always receives both fields
        // Use changedValues to detect explicit clearing (revops_id in changedValues means it was intentionally changed/cleared)
        veap_id: 'revops_id' in changedValues ? allValues['revops_id'] ?? '' : node.model.metadata?.veap_id ?? '',
        global: effectiveIsGlobalModel ? 'true' : 'false',
        platform: enableWebModel ? 'WebModel 2.0' : 'Model Repository',
      },
    };

    const updatedNode = {
      ...node,
      model: updatedModel,
    };

    setNode(updatedNode);
    setWorkingHierarchy((previousHierarchy) => {
      if (previousHierarchy.length === 0) {
        return [updatedNode];
      }
      if (!previousHierarchy.some((item) => item.model.uuid === updatedNode.model.uuid)) {
        return [...previousHierarchy, updatedNode];
      }
      return replaceNodeInFlatHierarchy(previousHierarchy, updatedNode);
    });

    onChange(updatedModel);
  };

  const handleRevopsSelect = (selectedRevopsId?: string) => {
    // Close the dropdown after selection
    setRevopsDropdownOpen(false);

    if (!selectedRevopsId) {
      // Only clear dependent fields if there was actually a REV/OPS ID set before
      const previousRevopsId = node.model.metadata?.revops_id || node.model.metadata?.veap_id;
      if (!previousRevopsId) {
        return;
      }

      setMdp([]);
      setLanguageAutoPopulated(false);
      userEditedLanguageRef.current = false;

      // COMPREHENSIVE CLEARING: Reset ALL fields that could have been auto-populated from RevOps/VEAP/Document
      const clearedValues = {
        // Direct RevOps fields
        revops_id: undefined,
        revops_name: undefined,

        // Fields from VEAP auto-fill
        model_development_plan: undefined,
        product: [],
        disease: undefined,
        therapeutic_area: undefined,
        stage_of_model_development: undefined, // Was set to 'MDP approved' by default

        // Fields from Document metadata auto-fill
        information_classification: undefined,
        type: undefined,
        model_type: undefined,
        country: undefined,
        version_release_of_model: undefined,
        study_number: undefined,

        // Owner hints (cleared from document metadata)
        owner: undefined,
        owners: undefined,
      };

      const mergedClearedValues = {
        ...form.getFieldsValue(),
        ...clearedValues,
      };

      form.setFieldsValue(clearedValues);
      handleFormChange(clearedValues, mergedClearedValues);

      // IMPORTANT: Reset country auto-population flag to allow re-auto-fill on next RevOps selection
      userEditedCountryOrGlobalRef.current = false;

      // Notify parent component that steward was reset (for permissions screen reset)
      onStewardReset?.({
        id: node.model.metadata?.isid_of_model_steward || '',
        email: node.model.metadata?.email_for_model_steward || '',
        name: node.model.metadata?.name_of_model_steward || '',
      });

      return;
    }

    // Find the selected VEAP item from revopsOptions
    const selectedVeapItem = revopsOptions.find((item) => item.id === selectedRevopsId);

    // Only allow selection of items with MDA prefix
    if (!selectedVeapItem || !selectedVeapItem.name?.startsWith('MDA')) {
      form.setFieldValue('revops_id', undefined);
      return;
    }

    // Get current form values to check what's already filled
    const currentValues = form.getFieldsValue();

    // Pre-populate fields from VEAP response only when target field is empty
    const autoFilledValues: any = {
      revops_id: selectedRevopsId,
      revops_name: selectedVeapItem.name, // Store the display name for review screen
      model_development_plan: undefined,
    };

    // Call the callback to update parent state with revops name
    onRevopsNameChange?.(selectedVeapItem.name);

    if (selectedVeapItem) {
      // Map VVMC fields to MR fields - only fill if empty per FR-05 and AC-09
      if (isEmptyValue(currentValues.disease) && selectedVeapItem.primaryIndication) {
        autoFilledValues.disease = selectedVeapItem.primaryIndication; // Indication -> Name of disease
      }
      if (isEmptyValue(currentValues.product) && selectedVeapItem.productName) {
        // Product name may come as a string, convert to array for multi-select
        const productArray = selectedVeapItem.productName.split('|').map((p) => p.trim());
        autoFilledValues.product = productArray; // Product -> Name of Product
      }
      if (isEmptyValue(currentValues.therapeutic_area) && selectedVeapItem.therapeuticArea) {
        autoFilledValues.therapeutic_area = selectedVeapItem.therapeuticArea; // Therap. Area -> Therapeutic Area
      }

      // Default stage when empty (VVMC mandatory metadata behavior)
      if (isEmptyValue(currentValues.stage_of_model_development)) {
        autoFilledValues.stage_of_model_development = 'MDP approved';
      }
    }

    const mergedValues = {
      ...form.getFieldsValue(),
      ...autoFilledValues,
    };

    form.setFieldsValue(autoFilledValues);
    handleFormChange(autoFilledValues, mergedValues);

    // Load MDP URLs from Veeva API and auto-select the first if the field is empty
    loadMdpList(selectedRevopsId).then((urls) => {
      if (urls.length > 0 && isEmptyValue(form.getFieldValue('model_development_plan'))) {
        const firstUrl = urls[0];
        form.setFieldValue('model_development_plan', firstUrl);
        handleMdpSelect(firstUrl);
        const mdpMergedValues = {
          ...form.getFieldsValue(),
          model_development_plan: firstUrl,
        };
        handleFormChange({model_development_plan: firstUrl}, mdpMergedValues);
      }
    });

    // Load document-level metadata from Veeva and populate only empty fields (non-blocking, graceful error handling)
    loadDocMetadata([selectedRevopsId])
      .then((rawDocMetadata) => {
        const docMetadata = rawDocMetadata as VeevaDocMetadata | null;
        if (!docMetadata) {
          // No document metadata found (404 or empty), continue without error
          return;
        }
        // Populate only empty fields: keep user-entered values and VEAP-derived values with precedence
        const latestFormValues = form.getFieldsValue();
        const docAutoFilledValues: any = {};
        let globalOverride: boolean | undefined;

        // Only auto-fill fields that are currently empty (enforce precedence: user > VEAP > document)
        if (isEmptyValue(latestFormValues.disease) && docMetadata.disease) {
          docAutoFilledValues.disease = docMetadata.disease;
        }
        if (isEmptyValue(latestFormValues.therapeutic_area) && docMetadata.therapeuticArea) {
          docAutoFilledValues.therapeutic_area = docMetadata.therapeuticArea;
        }
        if (isEmptyValue(latestFormValues.model_type) && docMetadata.modelType) {
          docAutoFilledValues.model_type = docMetadata.modelType;
        }
        if (!userEditedCountryOrGlobalRef.current) {
          const {country: resolvedCountry, isGlobal} = resolveCountryGlobal(docMetadata.country);
          docAutoFilledValues.country = resolvedCountry;
          globalOverride = isGlobal;
        }
        // Map classification to information_classification if empty (AC-04)
        if (isEmptyValue(latestFormValues.information_classification) && docMetadata.classification) {
          docAutoFilledValues.information_classification = docMetadata.classification;
        }
        // Auto-fill version_release_of_model from Veeva document version if empty
        if (isEmptyValue(latestFormValues.version_release_of_model) && docMetadata.version) {
          docAutoFilledValues.version_release_of_model = docMetadata.version;
        }
        // Propagate owner hints for downstream permission step
        if (docMetadata.owner) {
          if (isEmptyValue(latestFormValues.owner)) {
            docAutoFilledValues.owner = docMetadata.owner;
          }
        }
        if (
          Array.isArray(docMetadata.owners) &&
          docMetadata.owners.length > 0 &&
          isEmptyValue(latestFormValues.owners)
        ) {
          docAutoFilledValues.owners = docMetadata.owners;
        }
        // product field has precedence from VEAP, don't override with document metadata

        // Apply document metadata auto-filled values only if there are any
        if (Object.keys(docAutoFilledValues).length > 0) {
          form.setFieldsValue(docAutoFilledValues);
          const docMergedValues = {
            ...latestFormValues,
            ...docAutoFilledValues,
          };
          handleFormChange(docAutoFilledValues, docMergedValues, globalOverride);
        }
      })
      .catch(() => {
        // Non-blocking error: document metadata fetch failed (502, 401, 403, or other error)
        // Continue silently without interrupting the user workflow
        // User can still complete the form with VEAP-derived values
      });
  };

  const handleMdpSelect = (selectedMdpUrl?: string) => {
    if (!selectedMdpUrl) {
      setLanguageAutoPopulated(false);
      return;
    }

    if (userEditedLanguageRef.current) {
      setLanguageAutoPopulated(false);
      return;
    }

    const knownLanguages: string[] = hardCodedOptions.type ?? [];
    loadMdpModelLanguage([selectedMdpUrl, knownLanguages])
      .then((rawResponse) => {
        const response = rawResponse as {modelLanguage?: string | null} | null;
        const detectedLanguage = response?.modelLanguage;
        if (detectedLanguage && knownLanguages.includes(detectedLanguage)) {
          form.setFieldValue('type', detectedLanguage);
          const updatedValues = {
            ...form.getFieldsValue(),
            type: detectedLanguage,
          };
          handleFormChange({type: detectedLanguage}, updatedValues);
          setLanguageAutoPopulated(true);
          return;
        }
        setLanguageAutoPopulated(false);
      })
      .catch(() => {
        setLanguageAutoPopulated(false);
      });
  };

  const handleFinish = (values: any) => {
    setSubmitClicked(true);

    // Get all form field values including unmodified initial values
    //const allFormValues = form.getFieldsValue();

    // Build the updated model with all form values
    const updatedModel = {
      ...node.model,
      metadata: {
        ...node.model.metadata,
        ...Object.keys(values).reduce((acc, key) => {
          // Form field names now match metadata keys directly
          if (key === 'product' && Array.isArray(values[key])) {
            acc['product'] = values[key].join('|');
          } else {
            acc[key] = values[key];
          }
          return acc;
        }, {} as any),
        // Mirror revops_id → veap_id so the backend always receives both fields
        veap_id: values['revops_id'] ?? node.model.metadata?.veap_id ?? '',
        global: isGlobalModel ? 'true' : 'false',
        platform: enableWebModel ? 'WebModel 2.0' : 'Model Repository',
      },
    };

    // Always submit the form - let parent component handle validation if needed
    onSubmit(updatedModel);
  };

  return (
    <div className={styles.stepMetadataRedesigned}>
      <div className={styles.stepMetadataHeader}>
        <h2 className={styles.pageTitle}>Fill in Metadata</h2>
        <p className={styles.pageSubtitle}>
          It helps us find the lineage and version, and generate model entry name for you
        </p>
      </div>

      {/* Generated Model Name Display */}
      {model.name && (
        <div className={styles.generatedNameBanner}>
          <span className={styles.generatedNameLabel}>Generated Model Entry Name: </span>
          <span className={styles.generatedNameValue}>{model.name}</span>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
        onFinish={handleFinish}
        className={styles.metadataForm}
      >
        {/* Model Identification */}
        <div className={styles.formSection}>
          <div className={styles.sectionHeader}>
            <h4 className={styles.sectionTitle}>Model Identification</h4>
          </div>

          {/* Row 1: REV/OPS ID | Name of Product */}
          <Row gutter={40}>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    {metadataFieldLabels['revops_id']} {/* removed required asterisk for revops_id */}{' '}
                    <Tooltip title="Select to auto-populate related metadata fields">
                      <InfoCircleOutlined />
                    </Tooltip>{' '}
                    <span style={{fontWeight: 400, color: '#8c8c8c', fontSize: '12px'}}>
                      (Select to auto-populate related metadata fields)
                    </span>
                  </span>
                }
                name="revops_id"
                required={false}
                rules={[]}
                colon={false}
              >
                <Select
                  placeholder="Find Rev/Ops ID"
                  allowClear
                  showSearch
                  popupClassName={styles.reVopsSelectDropdown}
                  optionLabelProp="shortLabel"
                  loading={revopsLoading}
                  open={revopsDropdownOpen}
                  onDropdownVisibleChange={setRevopsDropdownOpen}
                  onSearch={(value) => loadRevopsList(value)}
                  onChange={handleRevopsSelect}
                  filterOption={false}
                  notFoundContent="No options"
                >
                  {revopsOptions.map((item) => (
                    <Option key={item.id} value={item.id} shortLabel={`${item.name}`}>
                      <div className={styles.revopsOptionItem}>
                        <div>
                          <Text strong>VEAP ID:</Text> {highlightText(item.name || '', revopsSearchTerm)}
                        </div>
                        <div>
                          <Text strong>Title:</Text> {highlightText(item.projectTitle || '', revopsSearchTerm)}
                        </div>
                        <div>
                          <Text strong>Contact:</Text> {item.keyContact}
                        </div>
                        <div>
                          <Text strong>Indication:</Text>{' '}
                          {highlightText(item.primaryIndication || '', revopsSearchTerm)}
                        </div>
                        <div>
                          <Text strong>Product:</Text> {highlightText(item.productName || '', revopsSearchTerm)}
                        </div>
                        <div>
                          <Text strong>Therapeutic Area:</Text>{' '}
                          {highlightText(item.therapeuticArea || '', revopsSearchTerm)}
                        </div>
                        <div>
                          <Text strong>MK/V Number:</Text> {item.mkvNumber?.join(', ')}
                        </div>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    {metadataFieldLabels['product']}{' '}
                    {!!metadataRequiredFields['product' as FieldKey] && <span style={{color: '#ff4d4f'}}>*</span>}
                  </span>
                }
                name="product"
                required={false}
                rules={[
                  {
                    required: !!metadataRequiredFields['product' as FieldKey],
                    message: `${metadataFieldLabels['product']} is required`,
                  },
                ]}
                colon={false}
              >
                <TreeSelect
                  placeholder="Select Product"
                  allowClear
                  treeCheckable
                  showCheckedStrategy={TreeSelect.SHOW_PARENT}
                  showSearch
                  filterTreeNode={(input, node) => {
                    const title = node?.title?.toString() || '';
                    return title.toLowerCase().includes(input.toLowerCase());
                  }}
                  treeData={options?.products?.map((product) => ({
                    title: product,
                    value: product,
                    key: product,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: Trial Number | Name of Disease */}
          <Row gutter={40}>
            <Col span={12}>
              <Form.Item label={metadataFieldLabels['study_number']} name="study_number">
                <Select
                  placeholder="Find Trial Number"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {options?.studyNumbers?.map((studyNumber) => (
                    <Option key={studyNumber} value={studyNumber}>
                      {studyNumber}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    {metadataFieldLabels['disease']}{' '}
                    {!!metadataRequiredFields['disease' as FieldKey] && <span style={{color: '#ff4d4f'}}>*</span>}
                  </span>
                }
                name="disease"
                required={false}
                rules={[
                  {
                    required: !!metadataRequiredFields['disease' as FieldKey],
                    message: `${metadataFieldLabels['disease']} is required`,
                  },
                ]}
                colon={false}
              >
                <Select
                  placeholder="Select Name of Disease"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {options?.diseases?.map((disease) => (
                    <Option key={disease} value={disease}>
                      {disease}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Row 3: Model Language | Therapeutic Area */}
          <Row gutter={40}>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    {metadataFieldLabels['type']}{' '}
                    {!!metadataRequiredFields['type' as FieldKey] && <span style={{color: '#ff4d4f'}}>*</span>}
                  </span>
                }
                name="type"
                extra={
                  languageAutoPopulated
                    ? 'Auto-populated from Model Development Plan. You can still override this value.'
                    : undefined
                }
                required={false}
                rules={[
                  {
                    required: !!metadataRequiredFields['type' as FieldKey],
                    message: `${metadataFieldLabels['type']} is required`,
                  },
                ]}
                colon={false}
              >
                <Select
                  placeholder="Find Model Language"
                  allowClear
                  showSearch
                  onChange={() => {
                    userEditedLanguageRef.current = true;
                    setLanguageAutoPopulated(false);
                  }}
                  filterOption={(input, option) =>
                    (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {hardCodedOptions.type?.map((type) => (
                    <Option key={type} value={type}>
                      {type === 'ExcelNative' ? 'Excel' : type}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    {metadataFieldLabels['therapeutic_area']}{' '}
                    {!!metadataRequiredFields['therapeutic_area' as FieldKey] && (
                      <span style={{color: '#ff4d4f'}}>*</span>
                    )}
                  </span>
                }
                name="therapeutic_area"
                required={false}
                rules={[
                  {
                    required: !!metadataRequiredFields['therapeutic_area' as FieldKey],
                    message: `${metadataFieldLabels['therapeutic_area']} is required`,
                  },
                ]}
                colon={false}
              >
                <Select
                  placeholder="Select Therapeutic Area"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {options?.therapeuticAreas?.map((area) => (
                    <Option key={area} value={area}>
                      {area}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Row 4: Information Classification | Model Type */}
          <Row gutter={40}>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    {metadataFieldLabels['information_classification']}{' '}
                    {!!metadataRequiredFields['information_classification' as FieldKey] && (
                      <span style={{color: '#ff4d4f'}}>*</span>
                    )}
                  </span>
                }
                name="information_classification"
                required={false}
                rules={[
                  {
                    required: !!metadataRequiredFields['information_classification' as FieldKey],
                    message: `${metadataFieldLabels['information_classification']} is required`,
                  },
                ]}
                colon={false}
              >
                <Select
                  placeholder="Select Information Classification"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {hardCodedOptions.information_classification?.map((classification) => (
                    <Option key={classification} value={classification}>
                      {classification}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    {metadataFieldLabels['model_type']}{' '}
                    {!!metadataRequiredFields['model_type' as FieldKey] && <span style={{color: '#ff4d4f'}}>*</span>}
                  </span>
                }
                name="model_type"
                required={false}
                rules={[
                  {
                    required: !!metadataRequiredFields['model_type' as FieldKey],
                    message: `${metadataFieldLabels['model_type']} is required`,
                  },
                ]}
                colon={false}
              >
                <Select
                  placeholder="Select Model Type"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {options?.modelTypes?.map((type) => (
                    <Option key={type} value={type}>
                      {type}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Row 5: (empty) | Enable WebModel 2.0 */}
          <Row gutter={40}>
            <Col span={12} />
            <Col span={12}>
              <Form.Item
                className={styles.webModelToggleItem}
                label={
                  <span>
                    Enable WebModel 2.0{' '}
                    <Tooltip title="WebModel 2.0 is a set of features in Model Repository that enables model deployment, execution, and visualization for scripted language models (e.g., Mathematica).">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </span>
                }
              >
                <Switch checked={enableWebModel} onChange={setEnableWebModel} />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Country Association */}
        <div className={styles.formSection}>
          <div className={styles.sectionHeader}>
            <h4 className={styles.sectionTitle}>Country Association</h4>
          </div>
          <Row gutter={40}>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    {metadataFieldLabels['country']}{' '}
                    {!!metadataRequiredFields['country' as FieldKey] && !isGlobalModel && (
                      <span style={{color: '#ff4d4f'}}>*</span>
                    )}
                  </span>
                }
                name="country"
                required={false}
                rules={[
                  {
                    required: !!metadataRequiredFields['country' as FieldKey] && !isGlobalModel,
                    message: `${metadataFieldLabels['country']} is required`,
                  },
                ]}
                colon={false}
              >
                <Select
                  placeholder="Select Country"
                  allowClear
                  showSearch
                  onChange={(value) => {
                    userEditedCountryOrGlobalRef.current = true;
                    const updatedValues = {
                      ...form.getFieldsValue(),
                      country: value,
                    };
                    handleFormChange({country: value}, updatedValues);
                    form.validateFields(['country']);
                  }}
                  filterOption={(input, option) =>
                    (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {options?.countries?.map((country) => (
                    <Option key={country} value={country}>
                      {country}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    Is this a Global Model?{' '}
                    <Tooltip title="Global models are not country-specific">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </span>
                }
              >
                <Switch
                  checked={isGlobalModel}
                  onChange={(checked) => {
                    userEditedCountryOrGlobalRef.current = true;
                    setIsGlobalModel(checked);
                    const currentCountry = form.getFieldValue('country');
                    const updatedValues = {
                      ...form.getFieldsValue(),
                      country: currentCountry,
                    };
                    handleFormChange({}, updatedValues, checked);
                    form.validateFields(['country']);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Model Suffix */}
        <div className={styles.formSection}>
          <div className={styles.sectionHeader}>
            <h4 className={styles.sectionTitle}>Model Suffix</h4>
          </div>
          <p className={styles.sectionHint}>Provide additional context or identifier for this model entry</p>
          <Form.Item name="suffix">
            <Input placeholder="Enter a brief description or identifier" />
          </Form.Item>
        </div>

        {/* Documentation */}
        <div className={styles.formSection}>
          <div className={styles.sectionHeader}>
            <h4 className={styles.sectionTitle}>Documentation</h4>
          </div>
          <Form.Item label={metadataFieldLabels['objective_of_model']} name="objective_of_model">
            <TextArea rows={4} placeholder="Enter the objective of your model" />
          </Form.Item>
        </div>

        {/* Notes */}
        <div className={styles.formSection}>
          <div className={styles.sectionHeader}>
            <h4 className={styles.sectionTitle}>Notes</h4>
          </div>
          <Form.Item label={metadataFieldLabels['notes']} name="notes">
            <TextArea rows={4} placeholder="Notes" />
          </Form.Item>
          <Form.Item
            label={
              <span>
                Training and Publication Links{' '}
                <span style={{fontWeight: 400, color: '#8c8c8c'}}>
                  (links to model publications, links to training recordings, etc.)
                </span>
              </span>
            }
            name="training_and_publication_link"
          >
            <TextArea rows={4} placeholder="Links" />
          </Form.Item>
          {trainingAndPublicationLinkValue && (
            <div
              style={{
                marginTop: '-16px',
                marginBottom: '24px',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '8px 12px',
                background: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
              }}
            >
              {trainingAndPublicationLinkValue
                .split('\n')
                .filter((line: string) => line.trim() !== '')
                .map((line: string, idx: number) => {
                  const urlRegex = /https?:\/\/[^\s]+/g;
                  const segments: Array<{type: 'text' | 'url'; value: string}> = [];
                  let lastIndex = 0;
                  let match;
                  while ((match = urlRegex.exec(line.trim())) !== null) {
                    if (match.index > lastIndex)
                      segments.push({type: 'text', value: line.trim().slice(lastIndex, match.index)});
                    segments.push({type: 'url', value: match[0]});
                    lastIndex = match.index + match[0].length;
                  }
                  if (lastIndex < line.trim().length)
                    segments.push({type: 'text', value: line.trim().slice(lastIndex)});
                  return (
                    <span key={idx} style={{wordBreak: 'break-all'}}>
                      {segments.map((seg, segIdx) =>
                        seg.type === 'url' ? (
                          <a
                            key={segIdx}
                            href={seg.value}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{fontSize: '13px', color: '#006A63', textDecoration: 'underline'}}
                          >
                            {seg.value}
                          </a>
                        ) : (
                          <Text key={segIdx} style={{fontSize: '13px', color: '#595959'}}>
                            {seg.value}
                          </Text>
                        ),
                      )}
                    </span>
                  );
                })}
            </div>
          )}
        </div>

        {/* Version Number */}
        <div className={styles.formSection}>
          <div className={styles.sectionHeader}>
            <h4 className={styles.sectionTitle}>Version Number</h4>
          </div>
          <Row gutter={40}>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    {metadataFieldLabels['version_release_of_model']}{' '}
                    {!!metadataRequiredFields['version_release_of_model' as FieldKey] && (
                      <span style={{color: '#ff4d4f'}}>*</span>
                    )}
                  </span>
                }
                name="version_release_of_model"
                required={false}
                rules={[
                  {
                    required: !!metadataRequiredFields['version_release_of_model' as FieldKey],
                    message: `${metadataFieldLabels['version_release_of_model']} is required`,
                  },
                ]}
                colon={false}
              >
                <Input placeholder="1.1" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    {metadataFieldLabels['changeLogFileName']}{' '}
                    <Tooltip title="This field represents the document version of the Version Log text box. If the text box is too short, you can upload a file with detailed change descriptions.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </span>
                }
                name="changelog_file"
              >
                <Select placeholder="Select Documents" allowClear>
                  {files?.map((file) => (
                    <Option key={file.uuid} value={file.uuid}>
                      {file.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label={metadataFieldLabels['version_log']} name="version_log">
            <TextArea rows={4} placeholder="Describe changes or updates from prior versions" />
          </Form.Item>
        </div>

        {/* Stage */}
        <div className={styles.formSection}>
          <div className={styles.sectionHeader}>
            <h4 className={styles.sectionTitle}>Stage</h4>
          </div>
          <Row gutter={40}>
            <Col span={12}>
              <Form.Item label={metadataFieldLabels['model_development_plan']} name="model_development_plan">
                <Select
                  placeholder="Select Model Development Plan"
                  allowClear
                  onChange={handleMdpSelect}
                  loading={mdp === undefined}
                  notFoundContent="No options"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {(mdp || []).map((url) => (
                    <Option key={url} value={url}>
                      {url}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    {metadataFieldLabels['stage_of_model_development']}{' '}
                    {!!metadataRequiredFields['stage_of_model_development' as FieldKey] && (
                      <span style={{color: '#ff4d4f'}}>*</span>
                    )}
                  </span>
                }
                name="stage_of_model_development"
                required={false}
                rules={[
                  {
                    required: !!metadataRequiredFields['stage_of_model_development' as FieldKey],
                    message: `${metadataFieldLabels['stage_of_model_development']} is required`,
                  },
                ]}
                colon={false}
              >
                <Select
                  placeholder="Select Stage of Model Development"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {options?.stagesOfModelDevelopment?.map((stage) => (
                    <Option key={stage} value={stage}>
                      {stage}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Link My Model Entry */}
        <div className={styles.formSection}>
          <div className={styles.sectionHeader}>
            <h4 className={styles.sectionTitle}>Link My Model Entry</h4>
          </div>
          {lineageHierarchy.some((n) => n.model.uuid !== model.uuid) && (
            <p className={styles.sectionHint}>Based on your metadata, the following possible parents were found.</p>
          )}
          <p className={styles.sectionHint}>
            Link the parent entry. All internal users will see this link.
            <br />
            Only people you grant permission will see the files for your entry.
          </p>

          {models && (
            <LineageSelectionAntd
              model={node.model}
              flatHierarchy={lineageHierarchy}
              files={files}
              onParentSelect={handleParentSelect}
              onClearParent={handleClearParent}
              onNewHierarchy={handleNewHierarchy}
              onRootSelect={handleRootSelect}
              currentMode={lineageMode}
              onModeChange={(mode) => {
                setLineageMode(mode);
                setSubmitClicked(false);
              }}
              isModelInHierarchy={isModelInHierarchy}
              submitClicked={submitClicked}
            />
          )}
        </div>

        {/* Footer Buttons */}
        {buttons &&
          buttons({
            onBack,
            disableNext: !!models && lineageMode !== 'parent',
            nextHtmlType: 'submit',
          })}
      </Form>
    </div>
  );
};

export default StepMetadataRedesigned;
